import { Injectable, inject } from '@angular/core';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { escapeRegex } from '@/src/app/uygulamalar/yuz-tanima/utils/regex.utils';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private FikoYT = inject(FikoYTService);
  private req = inject(FikoYTRequestService);

  /** Public aggregate - disaridan kullanan bilesenler icin wrapper */
  aggregate(komut: string, pipeline: any[]): Promise<any[]> {
    return this.req.aggregate(komut, pipeline);
  }

  // Dashboard metrikleri
  async dashboardMetrikleri(): Promise<any> {
    const [kullaniciSayisi, icerikSayisi, kuyrukDurumu, yuzIstatistik] = await Promise.all([
      this.kullaniciSayisi(),
      this.icerikSayisi(),
      this.kuyrukDurumu(),
      this.yuzTanimaIstatistik(),
    ]);
    return { kullaniciSayisi, icerikSayisi, kuyrukDurumu, yuzIstatistik };
  }

  async kullaniciSayisi(): Promise<number> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', [
      { $match: { silindi: { $ne: true } } },
      { $count: 'toplam' },
    ]);
    return sonuc?.[0]?.toplam || 0;
  }

  async icerikSayisi(): Promise<{ toplam: number; bugun: number }> {
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          bugun: [
            { $match: { olusturulmaTarihi: { $gte: bugun.toISOString() } } },
            { $count: 'sayi' },
          ],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      bugun: facet?.bugun?.[0]?.sayi || 0,
    };
  }

  async kuyrukDurumu(): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_kuyruk.aggregate', [
      { $group: { _id: '$durum', sayi: { $sum: 1 } } },
    ]);
    const durumlar: any = { bekliyor: 0, isleniyor: 0, tamamlandi: 0, hata: 0 };
    (sonuc || []).forEach((d: any) => {
      if (durumlar.hasOwnProperty(d._id)) durumlar[d._id] = d.sayi;
    });
    return durumlar;
  }

  async yuzTanimaIstatistik(): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_tespit.aggregate', [
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          toplamYuz: [{ $group: { _id: null, sayi: { $sum: '$yuz_sayisi' } } }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplamTespit: facet?.toplam?.[0]?.sayi || 0,
      toplamYuz: facet?.toplamYuz?.[0]?.sayi || 0,
    };
  }

  // Kullanici listesi
  async kullaniciListesi(sayfa = 1, limit = 20, filtre?: any): Promise<any> {
    const pipeline: any[] = [
      { $match: { silindi: { $ne: true }, ...(filtre || {}) } },
      { $sort: { olusturulmaTarihi: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
            {
              $lookup: {
                from: 'hareketler',
                let: { uid: '$_id' },
                pipeline: [
                  { $match: { $expr: { $eq: ['$sahip', '$$uid'] }, silindi: { $ne: true } } },
                  { $count: 'sayi' },
                ],
                as: '_icerik',
              },
            },
            { $addFields: { icerikSayisi: { $ifNull: [{ $arrayElemAt: ['$_icerik.sayi', 0] }, 0] } } },
            {
              $project: {
                kullaniciAdi: 1,
                'veri.pilesmi': 1,
                'veri.ePosta': 1,
                'veri.resimId': 1,
                'veri.cinsiyet': 1,
                yetkiler: 1,
                aktifDurum: 1,
                olusturulmaTarihi: 1,
                sonGiris: 1,
                banBilgi: 1,
                icerikSayisi: 1,
              },
            },
          ],
        },
      },
    ];
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', pipeline);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Yuz tanima kuyruk listesi
  async kuyrukListesi(sayfa = 1, limit = 20, durumFiltre?: string): Promise<any> {
    const match: any = {};
    if (durumFiltre) match.durum = durumFiltre;
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_kuyruk.aggregate', [
      { $match: match },
      { $sort: { olusturma: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
            {
              $lookup: {
                from: 'goUyeler',
                localField: 'kullanici_id',
                foreignField: '_id',
                as: 'kullanici',
              },
            },
            { $unwind: { path: '$kullanici', preserveNullAndEmptyArrays: true } },
            {
              $addFields: {
                kullaniciAdi: '$kullanici.kullaniciAdi',
                kullaniciResimId: '$kullanici.veri.resimId',
                resim_id: { $toString: '$resim_id' },
                kullanici_id: { $toString: '$kullanici_id' },
                tarih: { $ifNull: ['$tarih', '$olusturma'] },
              },
            },
            { $project: { kullanici: 0 } },
          ],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Taninan kisiler listesi
  async taninanKisiler(sayfa = 1, limit = 20): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_taninan.aggregate', [
      { $sort: { ad: 1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [{ $skip: (sayfa - 1) * limit }, { $limit: limit }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Tespit gecmisi
  async tespitGecmisi(sayfa = 1, limit = 20): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_tespit.aggregate', [
      { $sort: { tarih: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [{ $skip: (sayfa - 1) * limit }, { $limit: limit }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Tespit bilgisi (resim_id ile)
  async tespitByResimId(resimId: string): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_tespit.aggregate', [
      { $match: { resim_id: resimId } },
      { $limit: 1 },
    ]);
    return sonuc?.[0] || null;
  }

  // Son aktiviteler
  async sonAktiviteler(limit = 10): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $sort: { olusturulmaTarihi: -1 } },
      { $limit: limit },
      {
        $lookup: {
          from: 'goUyeler',
          localField: 'sahip',
          foreignField: '_id',
          as: 'kullanici',
        },
      },
      { $unwind: { path: '$kullanici', preserveNullAndEmptyArrays: true } },
      {
        $project: {
          sahip: 1,
          tur: 1,
          'ek.turu': 1,
          olusturulmaTarihi: 1,
          kullaniciAdi: '$kullanici.kullaniciAdi',
          pilesmi: '$kullanici.veri.pilesmi',
          cinsiyet: '$kullanici.veri.cinsiyet',
        },
      },
    ]);
  }

  // Kullanici rol guncelle
  async rolGuncelle(kullaniciId: string, yetkiler: string[]): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle',
      { _id: kullaniciId },
      { $set: { yetkiler } },
    );
  }

  // Kullanicinin uygulama erisimi + alt ozellik izinleri + fiyat override (goUyeler.veri)
  async kullaniciUygulamaErisim(kullaniciId: string): Promise<{ uygulamalar: string[]; altlar: string[]; fiyatlar: Record<string, number> }> {
    const r = await this.req.aggregate<any>('FikoYT.sistem.DB.goUyeler.aggregate', [
      { $match: { _id: kullaniciId } }, { $project: { 'veri.uygulamalar': 1, 'veri.uygulamaAltlari': 1, 'veri.fiyatlar': 1 } }, { $limit: 1 },
    ]);
    return { uygulamalar: r?.[0]?.veri?.uygulamalar || [], altlar: r?.[0]?.veri?.uygulamaAltlari || [], fiyatlar: r?.[0]?.veri?.fiyatlar || {} };
  }
  // Kullanicinin uygulama + alt ozellik erisimi + fiyat override guncelle
  async uygulamaErisimGuncelle(kullaniciId: string, uygulamalar: string[], altlar: string[], fiyatlar?: Record<string, number>): Promise<any> {
    const set: any = { 'veri.uygulamalar': uygulamalar, 'veri.uygulamaAltlari': altlar };
    if (fiyatlar) set['veri.fiyatlar'] = fiyatlar;
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle', { _id: kullaniciId }, { $set: set });
  }

  // Workbench + komut lisansi (oku) + fiyat override (veri.fiyatlar ortak)
  async kullaniciLisans(kullaniciId: string): Promise<{ workbenchler: string[]; komutlar: string[]; fiyatlar: Record<string, number> }> {
    const r = await this.req.aggregate<any>('FikoYT.sistem.DB.goUyeler.aggregate', [
      { $match: { _id: kullaniciId } }, { $project: { 'veri.workbenchler': 1, 'veri.komutlar': 1, 'veri.fiyatlar': 1 } }, { $limit: 1 },
    ]);
    return { workbenchler: r?.[0]?.veri?.workbenchler || [], komutlar: r?.[0]?.veri?.komutlar || [], fiyatlar: r?.[0]?.veri?.fiyatlar || {} };
  }
  // Workbench + komut lisansi (guncelle) + fiyat override
  async lisansGuncelle(kullaniciId: string, workbenchler: string[], komutlar: string[], fiyatlar?: Record<string, number>): Promise<any> {
    const set: any = { 'veri.workbenchler': workbenchler, 'veri.komutlar': komutlar };
    if (fiyatlar) set['veri.fiyatlar'] = fiyatlar;
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle', { _id: kullaniciId }, { $set: set });
  }

  // Kullanici durum guncelle
  async durumGuncelle(kullaniciId: string, aktif: boolean): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle',
      { _id: kullaniciId },
      { $set: { aktifDurum: aktif ? 'Aktif' : 'Pasif' } },
    );
  }

  // Yuz tanima kisi sil (cascade: orphan referanslari temizle)
  async yuzKisiSil(kisiId: string): Promise<any> {
    // 1. Kisiyi yuz_taninan koleksiyonundan sil
    const sonuc = await this.req.sil('FikoYT.sistem.DB.yuz_taninan.sil', { _id: kisiId });
    // 2. yuz_tespit koleksiyonundaki orphan eslesen_kisi_id referanslarini temizle
    //    eslesen_kisi_id, yuzler[] array'inin icindeki objelerde bir alan.
    //    $unset ile $[elem] kullanildiginda arrayFilters zorunludur,
    //    aksi halde MongoDB hata verir. arrayFilters eksenel parametresinde gonderilir.
    try {
      await this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
        { 'yuzler.eslesen_kisi_id': kisiId },
        { $unset: { 'yuzler.$[elem].eslesen_kisi_id': '' } },
        { arrayFilters: [{ 'elem.eslesen_kisi_id': kisiId }] },
      );
    } catch {}
    return sonuc;
  }

  // Bilinmeyen yuzler (etiketlenmemis tespitler - kisi_id'si olmayan)
  async bilinmeyenYuzler(sayfa = 1, limit = 20): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.yuz_tespit.aggregate', [
      { $unwind: '$yuzler' },
      { $match: { 'yuzler.eslesen_kisi_id': { $exists: false } } },
      { $sort: { tarih: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
            {
              $project: {
                resim_id: 1,
                tarih: 1,
                bbox: '$yuzler.bbox',
                descriptor: '$yuzler.descriptor',
              },
            },
          ],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Toplu yuz etiketle (delegated to yuzEtiketle at bottom)

  // Kisi icin yuz tespitlerini al (eslesen_kisi_id uzerinden)
  async kisiTespitleri(kisiId: string, limit = 20): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.yuz_tespit.aggregate', [
      { $unwind: { path: '$yuzler', preserveNullAndEmptyArrays: false } },
      { $match: { 'yuzler.eslesen_kisi_id': kisiId } },
      { $sort: { tarih: -1 } },
      { $limit: limit },
      {
        $project: {
          resim_id: 1,
          icerik_id: 1,
          tarih: 1,
          resim_boyut: 1,
          bbox: '$yuzler.bbox',
          mesafe: '$yuzler.mesafe',
          guven: '$yuzler.guven',
          eslesen_kisi_id: '$yuzler.eslesen_kisi_id',
        },
      },
    ]);
  }

  // Kisi birlestir: hedef kisi altinda birlestir
  async yuzKisiBirlestir(hedefId: string, kaynakId: string): Promise<any> {
    // 1. Tespitlerde kaynak ID'yi hedef ID ile degistir
    //    $[elem] kullanildigi icin arrayFilters zorunlu
    await this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
      { 'yuzler.eslesen_kisi_id': kaynakId },
      { $set: { 'yuzler.$[elem].eslesen_kisi_id': hedefId } },
      { arrayFilters: [{ 'elem.eslesen_kisi_id': kaynakId }] },
    );
    // 2. Kaynak kisiyi sil
    return this.req.sil('FikoYT.sistem.DB.yuz_taninan.sil', { _id: kaynakId });
  }

  // Kisi guncelle (ad, descriptor vb.)
  async yuzKisiGuncelle(kisiId: string, veri: any): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.yuz_taninan.guncelle',
      { _id: kisiId },
      { $set: veri },
    );
  }

  // Kuyruk ogesi sil
  async kuyrukSil(filter: any): Promise<any> {
    return this.req.sil('FikoYT.sistem.DB.yuz_kuyruk.sil', filter);
  }

  // Kuyruk ogesi durumunu guncelle
  async kuyrukDurumGuncelle(kuyrukId: string, durum: string, deneme = 0): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.yuz_kuyruk.guncelle',
      { _id: kuyrukId },
      { $set: { durum, deneme } },
    );
  }

  // Kuyruk toplu durum guncelle (orn: tüm 'isleniyor' → 'bekliyor')
  async kuyrukTopluDurumGuncelle(eskiDurum: string, yeniDurum: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.yuz_kuyruk.guncelle',
      { durum: eskiDurum },
      { $set: { durum: yeniDurum, deneme: 0 } },
    );
  }

  // ===== MODERASYON =====

  // Raporlanan icerikler
  async raporlananIcerikler(sayfa = 1, limit = 20, durumFiltre?: string, sebepFiltre?: string): Promise<any> {
    const match: any = {};
    if (durumFiltre && durumFiltre !== 'hepsi') match.durum = durumFiltre;
    if (sebepFiltre) match.sebep = sebepFiltre;
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.raporlar.aggregate', [
      { $match: match },
      { $sort: { tarih: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
            {
              $lookup: {
                from: 'hareketler',
                localField: 'icerikId',
                foreignField: '_id',
                as: 'icerik',
              },
            },
            { $unwind: { path: '$icerik', preserveNullAndEmptyArrays: true } },
            {
              $lookup: {
                from: 'goUyeler',
                localField: 'raporEden',
                foreignField: '_id',
                as: 'raporEdenKullanici',
              },
            },
            { $unwind: { path: '$raporEdenKullanici', preserveNullAndEmptyArrays: true } },
          ],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Rapor durumu guncelle
  async raporDurumGuncelle(raporId: string, durum: string, moderatorNot?: string): Promise<any> {
    const set: any = { durum, incelenmeTarihi: new Date().toISOString() };
    if (moderatorNot) set.moderatorNot = moderatorNot;
    return this.req.guncelle('FikoYT.sistem.DB.raporlar.guncelle',
      { _id: raporId },
      { $set: set },
    );
  }

  // Icerik sil (moderasyon)
  async icerikSil(icerikId: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
      { _id: icerikId },
      { $set: { silindi: true, silinmeTarihi: new Date().toISOString() } },
    );
  }

  // Kullanici uyar
  async kullaniciUyar(kullaniciId: string, sebep: string, seviye: 'uyari' | 'ciddi' | 'son'): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.kullanici_uyarilari.ekle', {
      kullaniciId,
      sebep,
      seviye,
      tarih: new Date().toISOString(),
    });
  }

  // Kullanici uyarilari
  async kullaniciUyarilari(kullaniciId: string): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.kullanici_uyarilari.aggregate', [
      { $match: { kullaniciId } },
      { $sort: { tarih: -1 } },
      { $limit: 50 },
    ]);
  }

  // Kullanici banla/askiya al
  async kullaniciBanla(kullaniciId: string, tur: 'ban' | 'askiya_al', sure?: number, sebep?: string): Promise<any> {
    const set: any = {
      aktifDurum: tur === 'ban' ? 'Banlandi' : 'Askida',
      banBilgi: {
        tur,
        sebep: sebep || '',
        baslangic: new Date().toISOString(),
        bitis: sure ? new Date(Date.now() + sure * 86400000).toISOString() : null,
      },
    };
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle',
      { _id: kullaniciId },
      { $set: set },
    );
  }

  // Ban kaldir
  async banKaldir(kullaniciId: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.goUyeler.guncelle',
      { _id: kullaniciId },
      { $set: { aktifDurum: 'Aktif' }, $unset: { banBilgi: '' } },
    );
  }

  // Banlanan kullanicilar
  async banlananKullanicilar(sayfa = 1, limit = 20): Promise<any> {
    return this.kullaniciListesi(sayfa, limit, {
      aktifDurum: { $in: ['Banlandi', 'Askida'] },
    });
  }

  // ===== DASHBOARD GELISMIS =====

  // Gunluk icerik trendi (son N gun)
  async icerikTrendi(gunSayisi = 7): Promise<any[]> {
    const baslangic = new Date();
    baslangic.setDate(baslangic.getDate() - gunSayisi);
    baslangic.setHours(0, 0, 0, 0);
    return this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { olusturulmaTarihi: { $gte: baslangic.toISOString() }, silindi: { $ne: true } } },
      {
        $group: {
          _id: { $substr: ['$olusturulmaTarihi', 0, 10] },
          sayi: { $sum: 1 },
        },
      },
      { $sort: { _id: 1 } },
    ]);
  }

  // Kullanici buyume trendi
  async kullaniciBuyume(gunSayisi = 30): Promise<any[]> {
    const baslangic = new Date();
    baslangic.setDate(baslangic.getDate() - gunSayisi);
    baslangic.setHours(0, 0, 0, 0);
    return this.req.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', [
      { $match: { olusturulmaTarihi: { $gte: baslangic.toISOString() } } },
      {
        $group: {
          _id: { $substr: ['$olusturulmaTarihi', 0, 10] },
          sayi: { $sum: 1 },
        },
      },
      { $sort: { _id: 1 } },
    ]);
  }

  // Icerik turu dagilimi
  async icerikTuruDagilimi(): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { silindi: { $ne: true } } },
      { $group: { _id: '$ek.turu', sayi: { $sum: 1 } } },
      { $sort: { sayi: -1 } },
    ]);
  }

  // Rapor istatistikleri
  async raporIstatistikleri(): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.raporlar.aggregate', [
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          bekleyen: [{ $match: { durum: 'bekliyor' } }, { $count: 'sayi' }],
          durumlar: [{ $group: { _id: '$durum', sayi: { $sum: 1 } } }],
          sebepler: [{ $group: { _id: '$sebep', sayi: { $sum: 1 } } }, { $sort: { sayi: -1 } }, { $limit: 5 }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      bekleyen: facet?.bekleyen?.[0]?.sayi || 0,
      durumlar: facet?.durumlar || [],
      sebepler: facet?.sebepler || [],
    };
  }

  // En aktif kullanicilar
  async enAktifKullanicilar(limit = 5): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { silindi: { $ne: true } } },
      { $group: { _id: '$sahip', sayi: { $sum: 1 } } },
      { $sort: { sayi: -1 } },
      { $limit: limit },
      {
        $lookup: {
          from: 'goUyeler',
          localField: '_id',
          foreignField: '_id',
          as: 'kullanici',
        },
      },
      { $unwind: { path: '$kullanici', preserveNullAndEmptyArrays: true } },
      {
        $project: {
          sayi: 1,
          kullaniciAdi: '$kullanici.kullaniciAdi',
          pilesmi: '$kullanici.veri.pilesmi',
        },
      },
    ]);
  }

  // Denetim kaydi ekle
  async denetimKaydiEkle(islem: string, detay: any): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.denetim_kaydi.ekle', {
      islem,
      detay,
      tarih: new Date().toISOString(),
      moderator: this.FikoYT.kimlik.aktifKullaniciJson()?._id,
    });
  }

  // Denetim kayitlari
  async denetimKayitlari(sayfa = 1, limit = 20): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.denetim_kaydi.aggregate', [
      { $sort: { tarih: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [{ $skip: (sayfa - 1) * limit }, { $limit: limit }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // Denetim kayitlari + moderator adi ($lookup goUyeler)
  async denetimKayitlariZengin(sayfa = 1, limit = 20): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.denetim_kaydi.aggregate', [
      { $sort: { tarih: -1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
            {
              $lookup: {
                from: 'goUyeler',
                localField: 'moderator',
                foreignField: '_id',
                as: 'moderatorBilgi',
              },
            },
            { $unwind: { path: '$moderatorBilgi', preserveNullAndEmptyArrays: true } },
            {
              $addFields: {
                moderatorAdi: { $ifNull: ['$moderatorBilgi.veri.pilesmi', '$moderatorBilgi.kullaniciAdi'] },
              },
            },
            { $project: { moderatorBilgi: 0 } },
          ],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  // ===== BILINMEYEN YUZLER =====

  // Yuz tespitindeki bir yuzun etiketini (eslesmeler) kaldir
  // tespitId: tespit kaydinin _id'si, yuzId: lightbox'tan gelen yuz.id (genelde "tespitId_yuz_0" formunda)
  // bbox parametresi opsiyonel: eger yuzId'den tespit bulunamazsa bbox ile eslestirme yapilir
  async yuzTespitEtiketSil(tespitId: string, yuzId?: string, bbox?: number[]): Promise<any> {
    // yuzId formatini parse et: "TESPIT_ID_yuz_INDEX" seklinde olabilir
    let gercekTespitId = tespitId;
    let yuzIndex = -1;
    if (yuzId) {
      const parts = yuzId.split('_yuz_');
      if (parts.length === 2) {
        gercekTespitId = parts[0];
        yuzIndex = parseInt(parts[1], 10);
      }
    }

    if (yuzIndex >= 0) {
      // Direkt index ile eslesmeler'i kaldir
      return this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
        { _id: gercekTespitId },
        { $unset: { [`yuzler.${yuzIndex}.eslesmeler`]: '' } },
      );
    }

    // Fallback: bbox ile eslestirme
    if (bbox && bbox.length >= 4) {
      return this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
        { _id: gercekTespitId, 'yuzler.bbox': bbox },
        { $unset: { 'yuzler.$.eslesmeler': '' } },
      );
    }

    // Son fallback: tespitId parametresini _id olarak kullan, ilk yuzun etiketini kaldir
    return this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
      { _id: tespitId },
      { $unset: { 'yuzler.0.eslesmeler': '' } },
    );
  }

  // Yuz tespitindeki yuze eslesme etiketi ekle
  async yuzEtiketle(tespitId: string, bbox: number[], kisiId: string, kisiAd: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.yuz_tespit.guncelle',
      { _id: tespitId, 'yuzler.bbox': bbox },
      { $set: { 'yuzler.$.eslesmeler': [{ ad: kisiAd, kisi_id: kisiId, mesafe: 0 }] } },
    );
  }

  // Yeni taninan kisi olustur
  async yeniKisiOlustur(veri: any): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.yuz_taninan.ekle', veri);
  }

  // ===== HMI PERSONEL + KART YONETIMI =====

  async hmiPersonelListesi(sayfa = 1, limit = 50, filtre?: any): Promise<any> {
    const pipeline: any[] = [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { silindi: { $ne: true }, ...(filtre || {}) } },
      { $sort: { isim: 1, soyisim: 1 } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          liste: [
            { $skip: (sayfa - 1) * limit },
            { $limit: limit },
          ],
        },
      },
    ];
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', pipeline);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      liste: facet?.liste || [],
    };
  }

  async hmiUyeAra(arama: string, limit = 10): Promise<any[]> {
    const regex = { $regex: escapeRegex(arama), $options: 'i' };
    return this.req.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', [
      {
        $match: {
          silindi: { $ne: true },
          $or: [
            { kullaniciAdi: regex },
            { 'veri.pilesmi': regex },
            { 'veri.ePosta': regex },
          ],
        },
      },
      { $limit: limit },
      {
        $project: {
          kullaniciAdi: 1,
          'veri.pilesmi': 1,
          'veri.ePosta': 1,
          'veri.resimId': 1,
          'veri.cinsiyet': 1,
        },
      },
    ]);
  }

  async hmiAlanOnerileri(alan: string): Promise<string[]> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { silindi: { $ne: true }, [alan]: { $exists: true, $ne: '' } } },
      { $group: { _id: `$${alan}` } },
      { $sort: { _id: 1 } },
      { $limit: 100 },
    ]);
    return (sonuc || []).map((d: any) => d._id).filter(Boolean);
  }

  async hmiPersonelEkle(veri: any): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.hareketler.ekle', {
      ...veri,
      vt: 'FikoYT.sistem.firma.personeller',
      aktif: true,
      olusturulmaTarihi: new Date().toISOString(),
    });
  }

  async hmiPersonelGuncelle(personelId: string, veri: any): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
      { _id: personelId },
      { $set: { ...veri, guncellemeTarihi: new Date().toISOString() } },
    );
  }

  async hmiPersonelSil(personelId: string): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
      { _id: personelId },
      { $set: { silindi: true, silinmeTarihi: new Date().toISOString() } },
    );
  }

  async hmiKartSorgula(kartUid: string): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { kartUid, aktif: true, silindi: { $ne: true } } },
      { $limit: 1 },
    ]);
    const personel = sonuc?.[0] || null;
    if (!personel?.kullaniciAdi) return personel;

    // FikoYT uye profilinden isim, soyisim, profilResmi cek
    try {
      const uye = await this.req.aggregate('FikoYT.sistem.DB.goUyeler.aggregate', [
        { $match: { kullaniciAdi: personel.kullaniciAdi } },
        { $limit: 1 },
      ]);
      const u = uye?.[0];
      if (u) {
        personel._uyeIsim = u.isim || '';
        personel._uyeSoyisim = u.soyIsim || '';
        personel._uyeResimId = u.profilResmi || '';
      }
    } catch {}

    return personel;
  }

  async hmiKartUidKontrol(kartUid: string, haricId?: string): Promise<boolean> {
    const match: any = { kartUid, silindi: { $ne: true } };
    if (haricId) match._id = { $ne: haricId };
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.firma.personeller', ...match } },
      { $count: 'sayi' },
    ]);
    return (sonuc?.[0]?.sayi || 0) > 0;
  }

  async hmiIstatistikler(): Promise<any> {
    const sonuc = await this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.firma.personeller' } },
      { $match: { silindi: { $ne: true } } },
      {
        $facet: {
          toplam: [{ $count: 'sayi' }],
          aktif: [{ $match: { aktif: true } }, { $count: 'sayi' }],
          kartli: [{ $match: { kartUid: { $exists: true, $ne: '' } } }, { $count: 'sayi' }],
          roller: [{ $group: { _id: '$rol', sayi: { $sum: 1 } } }, { $sort: { sayi: -1 } }],
        },
      },
    ]);
    const facet = sonuc?.[0];
    return {
      toplam: facet?.toplam?.[0]?.sayi || 0,
      aktif: facet?.aktif?.[0]?.sayi || 0,
      kartli: facet?.kartli?.[0]?.sayi || 0,
      roller: facet?.roller || [],
    };
  }

  // ============ CİHAZ GÜNCELLEMELERİ ============

  async cihazGuncelle(cihazId: string, veri: any): Promise<any> {
    return this.req.guncelle('FikoYT.sistem.DB.hareketler.guncelle',
      { _id: cihazId },
      { $set: { ...veri, guncellenmeTarihi: new Date().toISOString() } },
    );
  }

  // ============ IoT OLAY KAYITLARI ============

  /** Tüm IoT olaylarını saniye hassasiyetinde logla */
  async iotOlayKaydet(olay: {
    tip: string; // 'kart_koyuldu' | 'kart_cekildi' | 'cihaz_baglandi' | 'cihaz_koptu' | ...
    kartUid?: string;
    cihazKimlik?: string;
    cihazTipi?: string;
    personelId?: string;
    personelIsim?: string;
    detay?: any;
  }): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.hareketler.ekle', {
      ...olay,
      vt: 'FikoYT.sistem.cihaz.olaylar',
      zaman: new Date().toISOString(),
      zamanMs: Date.now(),
    });
  }

  /** Giriş/çıkış kaydı (PDKS) */
  async girisCikisKaydet(data: {
    kartUid: string;
    personelId?: string;
    personelIsim?: string;
    tur: 'giris' | 'cikis';
    cihazKimlik?: string;
  }): Promise<any> {
    return this.req.ekle('FikoYT.sistem.DB.hareketler.ekle', {
      ...data,
      vt: 'FikoYT.sistem.giris_cikis',
      zaman: new Date().toISOString(),
      zamanMs: Date.now(),
    });
  }

  /** Son olay kayıtlarını getir */
  async iotOlaylarGetir(limit = 100): Promise<any[]> {
    return this.req.aggregate('FikoYT.sistem.DB.hareketler.aggregate', [
      { $match: { vt: 'FikoYT.sistem.cihaz.olaylar' } },
      { $sort: { zamanMs: -1 } },
      { $limit: limit },
    ]);
  }

  // ============ ADMIN ÜYE + PAROLA ============

  // Admin: gecici parola ile uye ekle
  async kullaniciEkle(veri: {
    kullaniciAdi: string; isim: string; soyIsim: string; ePosta: string;
    yetkiler: string[]; geciciParola: string; epostaGonder: boolean;
  }): Promise<{ basari: boolean; kid?: string; mesaj?: string }> {
    const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.admin.uye.ekle' });
    const fd = new FormData();
    fd.append('req_body', JSON.stringify(veri));
    istek.parametreler = fd;
    try {
      await istek.isteSync();
      if (istek.cevap?.durum?.hata) {
        const hatalar = istek.cevap?.durum?.hatalar || [];
        //@ts-ignore
        const mesaj = hatalar.map((h: any) => h?.mesaj).filter(Boolean).join(' / ') || istek.cevap?.durum?.mesaj || 'Bilinmeyen sunucu hatasi';
        console.error('[kullaniciEkle] backend hata dondu:', hatalar, istek.cevap);
        return { basari: false, mesaj };
      }
      const s = (istek.cevap?.sonuc ?? istek.cevap) || {};
      if (!s.basari) {
        console.warn('[kullaniciEkle] basari=false, tam cevap:', istek.cevap);
      }
      return { basari: !!s.basari, kid: s.kid, mesaj: s.mesaj };
    } catch (err) {
      console.error('[kullaniciEkle] istek atilirken exception:', err);
      return { basari: false, mesaj: 'Istek gonderilirken beklenmeyen hata olustu' };
    }
  }

  // Oturumdaki kullanicinin parolasini degistir
  async sifreDegistir(mevcutParola: string, yeniParola: string, yeniParolaTekrar: string): Promise<{ basari: boolean }> {
    const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.sifre.degistir' });
    const fd = new FormData();
    fd.append('req_body', JSON.stringify({ mevcutParola, yeniParola, yeniParolaTekrar }));
    istek.parametreler = fd;
    try {
      await istek.isteSync();
      if (istek.cevap?.durum?.hata) return { basari: false };
      const s = (istek.cevap?.sonuc ?? istek.cevap) || {};
      return { basari: !!s.basari };
    } catch {
      return { basari: false };
    }
  }
}
